<template>
  <Form
    class="flex flex-col gap-y-4 w-full"
    :resolver="basicInfoResolver"
    :validateOnBlur="true"
    :validateOnValueUpdate="true"
    ref="basicInfoForm"
    v-slot="$form"
    v-focustrap
  >
    <LabeledField
      label="Company Name"
      :field="$form.name"
      data-test="company-name-register"
    >
      <InputText
        name="name"
        fluid
      />
    </LabeledField>
    <LabeledField
      label="Company Website"
      :field="$form.url"
      data-test="company-website-register"
    >
      <InputText
        name="url"
        type="url"
        fluid
        placeholder="www.website.com"
      />
    </LabeledField>
    <LabeledField
      label="Custom URL"
      :field="$form.customUrl"
      data-test="custom-url-field"
    >
      <InputGroup>
        <InputGroupAddon>
          <span
            class="text-gray-500"
            data-test="truncated-domain"
            >{{ truncatedDomain }}</span
          >
        </InputGroupAddon>
        <InputText
          name="customUrl"
          type="url"
          v-keyfilter="/[A-Za-z\-]/"
          fluid
          data-test="custom-url-input"
        />
      </InputGroup>
      <div class="flex text-sm text-gray-500 my-2">
        <i class="fas fa-info-circle mr-2 mt-[2px]"></i>
        <div data-test="custom-url-info">
          Custom URL will be used to access your company's giving page. It may contain only letters and hyphens.<br />
          Example: <span class="font-semibold">company-name</span>
        </div>
      </div>
    </LabeledField>
    <LabeledField
      label="About Us"
      :field="$form.aboutUs"
      data-test="about-us"
    >
      <Textarea
        name="aboutUs"
        autoResize
        fluid
        data-test="about-us-textarea"
      />
      <div class="flex text-sm text-gray-500 mt-1">
        <i class="fas fa-info-circle mr-2 mt-[2px]"></i>
        <div data-test="about-us-char-count">{{ ($form.aboutUs?.value || '').length }}/560 Characters</div>
      </div>
    </LabeledField>
    <LabeledField
      label="Company Goal"
      :field="$form.companyGoal"
      data-test="company-goal"
    >
      <InputNumber
        name="companyGoal"
        mode="currency"
        currency="USD"
        locale="en-US"
        fluid
        data-test="company-goal-input"
      />
    </LabeledField>
    <LabeledField
      label="Participation Text"
      :field="$form.participationText"
      data-test="participation-text"
    >
      <Textarea
        name="participationText"
        autoResize
        fluid
        data-test="participation-text-textarea"
      />
      <div class="flex text-sm text-gray-500 mt-1">
        <i class="fas fa-info-circle mr-2 mt-[2px]"></i>
        <div data-test="participation-char-count">{{ ($form.participationText?.value || '').length }}/560 Characters</div>
      </div>
    </LabeledField>
    <LabeledField
      label="Company Logo"
      :field="$form.logoImage"
      data-test="company-logo"
    >
      <FormField
        v-slot="$field"
        name="logoImage"
      >
        <ImageUpload
          auto
          name="$field.props.name"
          :minSize="400"
          :maxSize="1024"
          :value="$field"
          @uploaded="
            (data) => {
              $form.logoImage.value = data;
            }
          "
        />
      </FormField>
    </LabeledField>
  </Form>
</template>

<script setup>
import { ref, onMounted, computed } from 'vue';
import { z } from 'zod';
import { zodResolver } from '@primevue/forms/resolvers/zod';
import { useCompanyAdminStore } from '@/stores/CompanyAdminStore.js';
import ImageUpload from '@/components/forms/ImageUpload.vue';
import LabeledField from '@/components/forms/LabeledField.vue';
import { useDonorStore } from '@/stores/DonorStore.js';
import debounceAsync from '../../helpers/debounceAsync';

const basicInfoForm = ref(null);
const donorStore = useDonorStore();

const debouncedCheckUrlAvailability = debounceAsync(async (customUrl) => {
  return await useCompanyAdminStore().checkUrlAvailability(customUrl, 'initial');
}, 300);

const basicInfoResolver = zodResolver(
  z.object({
    name: z.string().min(3, 'Name must be at least 3 characters.'),
    url: z
      .string()
      .min(1, 'URL is required')
      .regex(/^(https?:\/\/)?(www\.)?[a-zA-Z0-9-]+(\.[a-zA-Z]{2,})+$/, 'Invalid URL format')
      .transform((url) => {
        if (!/^https?:\/\//.test(url)) {
          return `https://${url}`;
        }
        return url;
      }),
    customUrl: z
      .string()
      .min(3, 'Custom URL must be at least 3 characters.')
      .max(64, 'Custom URL must not exceed 64 characters.')
      .regex(/^[A-Za-z0-9\-]+$/, {
        message: 'Custom URL may contain only letters, numbers, and hyphens.',
      })
      .superRefine(async (customUrl, ctx) => {
        if (customUrl.length < 3) {
          return;
        }
        try {
          const { available, message } = await debouncedCheckUrlAvailability(customUrl);
          if (!available) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: message || 'An unknown error occurred.', 
            });
          }
        } catch (error) {
          if (error.response && error.response.data && error.response.data.errors?.customUrl) {
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: error.response.data.errors.customUrl[0], 
            });
          } else {
            // Handle unexpected errors
            ctx.addIssue({
              code: z.ZodIssueCode.custom,
              message: 'An unexpected error occurred while checking the URL.',
            });
          }
        }
      }),
    aboutUs: z.string().max(560, 'Maximum 560 characters allowed'),
    companyGoal: z.number().nonnegative('Goal cannot be negative').max(1000000, 'Goal exceeds maximum allowed amount'),
    participationText: z.string().max(560, 'Maximum 560 characters allowed'),
    logoImage: z.nullable(z.string()),
  }),
);

const truncatedDomain = computed(() => {
  let domain = donorStore.domain.replace(/\/+$/, '');

  domain = domain.replace(/^https?:\/\//, '');

  if (domain.length > 20) {
    domain = `${domain.slice(0, 20)}...`;
  }
  return domain + '/';
});

onMounted(() => {
  useCompanyAdminStore().basicInfoForm = basicInfoForm.value;
});
</script>
